// 原型间关系矩阵 - 用于计算契合度
// complement: 互补关系，tension: 张力/冲突，mirror: 镜像/相似

export const archetypeRelations = {
  ruler: {
    complement: ['caregiver', 'sage'],
    tension: ['outlaw', 'jester'],
    mirror: ['hero'],
  },
  hero: {
    complement: ['caregiver', 'innocent'],
    tension: ['jester', 'everyman'],
    mirror: ['ruler'],
  },
  sage: {
    complement: ['creator', 'explorer'],
    tension: ['innocent', 'jester'],
    mirror: ['magician'],
  },
  explorer: {
    complement: ['creator', 'sage'],
    tension: ['ruler', 'caregiver'],
    mirror: ['outlaw'],
  },
  outlaw: {
    complement: ['magician', 'creator'],
    tension: ['ruler', 'caregiver'],
    mirror: ['explorer'],
  },
  magician: {
    complement: ['innocent', 'creator'],
    tension: ['everyman', 'ruler'],
    mirror: ['sage'],
  },
  creator: {
    complement: ['explorer', 'magician'],
    tension: ['ruler', 'everyman'],
    mirror: ['lover'],
  },
  lover: {
    complement: ['caregiver', 'innocent'],
    tension: ['sage', 'outlaw'],
    mirror: ['creator'],
  },
  caregiver: {
    complement: ['hero', 'ruler'],
    tension: ['outlaw', 'explorer'],
    mirror: ['innocent'],
  },
  jester: {
    complement: ['everyman', 'explorer'],
    tension: ['ruler', 'hero'],
    mirror: ['innocent'],
  },
  everyman: {
    complement: ['jester', 'caregiver'],
    tension: ['hero', 'creator'],
    mirror: ['lover'],
  },
  innocent: {
    complement: ['hero', 'magician'],
    tension: ['outlaw', 'sage'],
    mirror: ['caregiver'],
  },
};

// 计算两人的契合度分析
export function calculateSynastry(scoresA, scoresB) {
  const topA = getTop(scoresA);
  const topB = getTop(scoresB);
  
  // 1. 向量相似度 (余弦相似度)
  const similarity = cosineSimilarity(scoresA, scoresB);
  
  // 2. 关系分析
  const relations = analyzeRelations(topA, topB);
  
  // 3. 综合契合度
  const compatibility = calculateCompatibility(similarity, relations);
  
  return {
    similarity: Math.round(similarity * 100),
    compatibility,
    topA,
    topB,
    relations,
  };
}

function getTop(scores) {
  return Object.entries(scores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([key]) => key);
}

function cosineSimilarity(a, b) {
  const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])];
  let dotProduct = 0, normA = 0, normB = 0;
  
  keys.forEach(k => {
    const va = a[k] || 0;
    const vb = b[k] || 0;
    dotProduct += va * vb;
    normA += va * va;
    normB += vb * vb;
  });
  
  if (normA === 0 || normB === 0) return 0;
  return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB));
}

function analyzeRelations(topA, topB) {
  const result = {
    complements: [],
    tensions: [],
    mirrors: [],
  };
  
  topA.forEach(a => {
    const rel = archetypeRelations[a];
    if (!rel) return;
    
    topB.forEach(b => {
      if (rel.complement.includes(b)) {
        result.complements.push({ from: a, to: b });
      }
      if (rel.tension.includes(b)) {
        result.tensions.push({ from: a, to: b });
      }
      if (rel.mirror.includes(b) || a === b) {
        result.mirrors.push({ from: a, to: b });
      }
    });
  });
  
  return result;
}

function calculateCompatibility(similarity, relations) {
  // 基础分：相似度
  let score = similarity * 50;
  
  // 互补加分
  score += relations.complements.length * 15;
  
  // 镜像加分（相似灵魂）
  score += relations.mirrors.length * 10;
  
  // 张力不一定是坏事，适度张力增加吸引力
  if (relations.tensions.length === 1) {
    score += 5;
  } else if (relations.tensions.length > 2) {
    score -= 10;
  }
  
  return Math.min(100, Math.max(0, Math.round(score)));
}

// 生成关系描述
export function generateDescription(synastry, archetypes) {
  const { compatibility, relations, topA, topB } = synastry;
  
  const nameA = archetypes[topA[0]]?.name || topA[0];
  const nameB = archetypes[topB[0]]?.name || topB[0];
  
  let title, desc;
  
  if (compatibility >= 80) {
    title = '灵魂共振';
    desc = `${nameA}与${nameB}之间存在深层的能量共鸣，你们的星球轨道几乎完美交织。`;
  } else if (compatibility >= 60) {
    title = '引力相吸';
    desc = `${nameA}与${nameB}形成稳定的双星系统，互补的特质让你们相互吸引。`;
  } else if (compatibility >= 40) {
    title = '平行轨道';
    desc = `${nameA}与${nameB}各自运行在独立轨道上，偶尔的交汇带来新鲜感。`;
  } else {
    title = '引力张力';
    desc = `${nameA}与${nameB}之间存在强烈的引力场干扰，这种张力可能带来冲突，也可能激发成长。`;
  }
  
  // 生成契合点
  const strengths = [];
  if (relations.complements.length > 0) {
    relations.complements.forEach(({ from, to }) => {
      const fromName = archetypes[from]?.name || from;
      const toName = archetypes[to]?.name || to;
      strengths.push(getComplementText(from, to, fromName, toName));
    });
  }
  if (relations.mirrors.length > 0) {
    relations.mirrors.forEach(({ from, to }) => {
      const fromName = archetypes[from]?.name || from;
      const toName = archetypes[to]?.name || to;
      if (from === to) {
        strengths.push(`你们都是${fromName}，拥有相同的灵魂频率，天然理解彼此`);
      } else {
        strengths.push(`${fromName}与${toName}是镜像原型，你们能在对方身上看到自己`);
      }
    });
  }
  
  // 生成冲突点
  const challenges = [];
  if (relations.tensions.length > 0) {
    relations.tensions.forEach(({ from, to }) => {
      const fromName = archetypes[from]?.name || from;
      const toName = archetypes[to]?.name || to;
      challenges.push(getTensionText(from, to, fromName, toName));
    });
  }
  
  return { title, desc, strengths, challenges };
}

// 互补关系的具体描述
function getComplementText(from, to, fromName, toName) {
  const complementTexts = {
    'ruler-caregiver': `${fromName}的领导力与${toName}的关怀形成完美配合，一个指引方向，一个温暖人心`,
    'ruler-sage': `${fromName}的决断力与${toName}的智慧相辅相成，决策更加周全`,
    'hero-caregiver': `${fromName}的勇气与${toName}的温柔互补，刚柔并济`,
    'hero-innocent': `${fromName}的保护欲与${toName}的纯真相遇，守护与被守护的美好关系`,
    'sage-creator': `${fromName}的深度思考与${toName}的创意表达碰撞出智慧火花`,
    'sage-explorer': `${fromName}的知识与${toName}的好奇心相遇，一起探索未知`,
    'explorer-creator': `${fromName}的冒险精神与${toName}的创造力结合，开拓新天地`,
    'outlaw-magician': `${fromName}的颠覆力量与${toName}的转化能力，一起改变世界`,
    'outlaw-creator': `${fromName}的反叛精神与${toName}的创新思维，打破常规创造新可能`,
    'magician-innocent': `${fromName}的愿景与${toName}的信任，梦想照进现实`,
    'magician-creator': `${fromName}的想象力与${toName}的执行力，把不可能变为可能`,
    'creator-explorer': `${fromName}的独特视角与${toName}的开放心态，创造无限可能`,
    'lover-caregiver': `${fromName}的深情与${toName}的付出，爱意满满的关系`,
    'lover-innocent': `${fromName}的浪漫与${toName}的纯真，童话般的美好`,
    'caregiver-hero': `${fromName}的关怀与${toName}的担当，相互支持的伙伴`,
    'caregiver-ruler': `${fromName}的温暖与${toName}的责任感，稳固的信任基础`,
    'jester-everyman': `${fromName}的幽默与${toName}的亲和力，轻松愉快的相处`,
    'jester-explorer': `${fromName}的玩心与${toName}的好奇心，一起发现生活的乐趣`,
    'everyman-jester': `${fromName}的真实与${toName}的轻松，没有压力的友谊`,
    'everyman-caregiver': `${fromName}的踏实与${toName}的温暖，让人安心的陪伴`,
    'innocent-hero': `${fromName}的信任与${toName}的勇气，被保护的安全感`,
    'innocent-magician': `${fromName}的纯真与${toName}的魔力，相信奇迹会发生`,
  };
  
  const key = `${from}-${to}`;
  const reverseKey = `${to}-${from}`;
  return complementTexts[key] || complementTexts[reverseKey] || 
    `${fromName}与${toName}的特质形成互补，能够弥补彼此的盲点`;
}

// 张力关系的具体描述
function getTensionText(from, to, fromName, toName) {
  const tensionTexts = {
    'ruler-outlaw': `${fromName}追求秩序，${toName}渴望打破规则，需要找到自由与责任的平衡`,
    'ruler-jester': `${fromName}的严肃与${toName}的玩世不恭可能产生摩擦，学会适时放松`,
    'hero-jester': `${fromName}的认真与${toName}的调侃可能冲突，理解幽默也是一种力量`,
    'hero-everyman': `${fromName}追求卓越，${toName}享受平凡，接纳不同的人生态度`,
    'sage-innocent': `${fromName}的理性可能刺破${toName}的美好幻想，温柔地分享真相`,
    'sage-jester': `${fromName}的深刻与${toName}的轻松可能互不理解，学会欣赏不同的智慧`,
    'explorer-ruler': `${fromName}渴望自由，${toName}强调责任，在冒险与稳定间找平衡`,
    'explorer-caregiver': `${fromName}想要远方，${toName}想要守护，理解彼此的需求`,
    'outlaw-caregiver': `${fromName}的叛逆与${toName}的传统可能冲突，尊重不同的价值观`,
    'magician-everyman': `${fromName}追求非凡，${toName}珍视平凡，接纳不同的生活方式`,
    'magician-ruler': `${fromName}的理想主义与${toName}的现实主义需要调和`,
    'creator-ruler': `${fromName}的自由创作与${toName}的规则意识可能碰撞，在创新与秩序间平衡`,
    'creator-everyman': `${fromName}追求独特，${toName}渴望归属，理解不同的自我表达方式`,
    'lover-sage': `${fromName}重感性，${toName}重理性，学会用心也用脑`,
    'lover-outlaw': `${fromName}渴望连接，${toName}习惯独立，在亲密与空间间找平衡`,
    'outlaw-innocent': `${fromName}的尖锐可能伤害${toName}的纯真，学会温柔地表达`,
  };
  
  const key = `${from}-${to}`;
  const reverseKey = `${to}-${from}`;
  return tensionTexts[key] || tensionTexts[reverseKey] || 
    `${fromName}与${toName}的某些特质存在张力，需要相互理解与包容`;
}
